/**
 * OrganizationMemberPicker — single-select combobox for org colleagues.
 *
 * Jun 16, 2026 — first surface: dashboard org-member share dialog. Built
 * here in ``@ando/ui`` so canvas/share/automation surfaces can reuse it
 * without re-implementing the autocomplete + a11y wiring.
 *
 * Design choices:
 *   - **Caller passes the member list** (no BE call inside the primitive).
 *     The host fetches via ``useOrgMembers`` and forwards. Keeps the
 *     primitive wire-agnostic + matches the ``ShareDialog`` boundary.
 *   - **Client-side filter** (substring on name + email). The BE returns
 *     the full org member set in one call (typical org < 100 members,
 *     verified pattern from ``ChatSyncFiltersSheet``).
 *   - **Single-select** for share use case. Multi-select can be added
 *     later via a ``mode`` prop without touching consumers.
 *   - **WAI-ARIA combobox-listbox** (Apple HIG / Material). Arrow
 *     keys + Enter + Esc; focus returns to the input on Esc.
 *   - **Hides inactive / self-row** by default. Owner can't share with
 *     themselves and inactive members shouldn't appear as targets.
 */

import * as React from "react";
import type { I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Check, ChevronDown, Search, User } from "lucide-react";

import { cn } from "../lib/cn";

export interface OrganizationMemberOption {
  /** Stable id — typically ``OrgMember.id``. */
  readonly id: string;
  /** ``OrgMember.user_id`` — null for pending invites. */
  readonly user_id: string | null;
  readonly name: string | null;
  readonly email: string | null;
  readonly is_active: boolean;
}

export interface OrganizationMemberPickerProps {
  /** Full member list (caller fetches via ``useOrgMembers``). */
  readonly members: readonly OrganizationMemberOption[];
  /** Currently selected user_id (``null`` ⇒ nothing selected). */
  readonly value: string | null;
  /** Emitted when a member is picked. ``null`` ⇒ user cleared the field. */
  readonly onChange: (userId: string | null, member: OrganizationMemberOption | null) => void;
  /** User_id strings to hide (e.g. caller themselves; existing recipients). */
  readonly excludeUserIds?: readonly string[];
  /** Visible placeholder. Override per surface for context-specific copy. */
  readonly placeholder?: string;
  /** Empty-state copy when no rows match the filter. */
  readonly noMatchesLabel?: string;
  /** Disabled state — picker is fully read-only. */
  readonly disabled?: boolean;
  /** Loading state (showing while ``useOrgMembers`` is in flight). */
  readonly isLoading?: boolean;
  /** Stable id for the ``<input>`` so a sibling ``<label htmlFor>`` works. */
  readonly id?: string;
  /** Accessible label when no visible ``<label>`` is rendered. */
  readonly ariaLabel?: string;
  /** Extra wrapper classes. */
  readonly className?: string;
}

function _label(m: OrganizationMemberOption, i18n: I18n): string {
  return m.name?.trim() || m.email?.trim() || i18n._(msg`Senza nome`);
}

function _initials(m: OrganizationMemberOption): string {
  const src = m.name?.trim() || m.email?.trim() || "?";
  return src
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s.charAt(0).toUpperCase())
    .join("");
}

export const OrganizationMemberPicker: React.FC<OrganizationMemberPickerProps> = ({
  members,
  value,
  onChange,
  excludeUserIds,
  placeholder,
  noMatchesLabel,
  disabled,
  isLoading,
  id,
  ariaLabel,
  className,
}) => {
  const { i18n } = useLingui();
  const resolvedPlaceholder = placeholder ?? i18n._(msg`Cerca un collega…`);
  const resolvedNoMatchesLabel = noMatchesLabel ?? i18n._(msg`Nessun collega trovato.`);
  const resolvedAriaLabel = ariaLabel ?? i18n._(msg`Cerca un membro dell'organizzazione`);
  const reactGeneratedId = React.useId();
  const inputId = id ?? reactGeneratedId;
  const listboxId = `${inputId}-listbox`;

  const [query, setQuery] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const excludeSet = React.useMemo(
    () => new Set((excludeUserIds ?? []).filter(Boolean)),
    [excludeUserIds],
  );

  const candidates = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return members
      .filter((m) => m.is_active && m.user_id !== null)
      .filter((m) => !excludeSet.has(m.user_id ?? ""))
      .filter((m) => {
        if (!q) return true;
        const hay = `${m.name ?? ""} ${m.email ?? ""}`.toLowerCase();
        return hay.includes(q);
      });
  }, [members, query, excludeSet]);

  // Reset activeIndex when the visible set shrinks below it.
  React.useEffect(() => {
    if (activeIndex >= candidates.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, candidates.length]);

  // Outside-click closes the listbox so it doesn't linger when the user
  // moves to another input on the page.
  React.useEffect(() => {
    if (!open) return;
    function onDocClick(event: MouseEvent) {
      const root = wrapperRef.current;
      if (root && !root.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const selectedMember = React.useMemo(
    () => members.find((m) => m.user_id === value) ?? null,
    [members, value],
  );

  // Display the selected member's label as the input value when closed +
  // nothing in the query buffer. Once the user starts typing the query
  // takes over so filtering feels natural.
  const displayValue = open ? query : selectedMember ? _label(selectedMember, i18n) : "";

  function pick(idx: number) {
    const member = candidates[idx];
    if (!member || member.user_id === null) return;
    onChange(member.user_id, member);
    setQuery("");
    setOpen(false);
    // Return focus to the trigger so keyboard users stay in place.
    inputRef.current?.blur();
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, candidates.length - 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      if (open && candidates.length > 0) {
        pick(activeIndex);
      } else {
        setOpen(true);
      }
      return;
    }
    // Backspace clears the selection ONLY when the listbox is open and the
    // input is empty (user explicitly typed nothing). When closed, the
    // input shows ``_label(selectedMember)`` as a label-only display —
    // hitting Backspace there should open the listbox so the user can
    // start filtering, not silently wipe the selection.
    if (event.key === "Backspace" && !query && selectedMember && open) {
      onChange(null, null);
      return;
    }
    if (event.key === "Backspace" && !open && selectedMember) {
      event.preventDefault();
      setOpen(true);
      return;
    }
  }

  return (
    <div
      ref={wrapperRef}
      className={cn("relative w-full", className)}
      data-component="organization-member-picker"
    >
      <div
        className={cn(
          "flex items-center gap-2 rounded-md border border-hairline bg-card/50 px-2.5 py-2",
          disabled && "cursor-not-allowed opacity-60",
        )}
      >
        <Search className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden />
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          aria-label={resolvedAriaLabel}
          aria-controls={listboxId}
          aria-expanded={open}
          aria-autocomplete="list"
          aria-activedescendant={
            open && candidates[activeIndex]
              ? `${listboxId}-opt-${candidates[activeIndex].id}`
              : undefined
          }
          value={displayValue}
          placeholder={resolvedPlaceholder}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActiveIndex(0);
          }}
          onKeyDown={onKeyDown}
          className={cn(
            "min-w-0 flex-1 bg-transparent text-[12.5px] text-ink-1 outline-none placeholder:text-ink-3",
            "disabled:cursor-not-allowed",
          )}
        />
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-ink-3 transition-transform",
            open && "rotate-180",
          )}
          aria-hidden
        />
      </div>

      {/*
        WAI-ARIA 1.2 combobox: the listbox referenced by ``aria-controls``
        MUST exist in the DOM at all times so SR can find it. We render
        the element unconditionally and toggle visibility via the
        ``hidden`` attribute — Radix and shadcn ``Command`` use the
        same approach.
      */}
      {/*
        ``<div role="listbox">`` instead of ``<ul>`` — Biome's
        ``no-noninteractive-element-to-interactive-role`` flags
        ``<ul role="listbox">``/``<li role="option">`` as errors even
        though it is the canonical shadcn/Radix/react-aria pattern.
        Switching to a ``<div>`` carrier preserves ARIA semantics
        (the role override is what SRs read) without the lint
        violation. Same approach as cmdk + shadcn ``Command``.
      */}
      <div
        id={listboxId}
        role="listbox"
        hidden={!open || disabled}
        className={cn(
          "absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-hairline bg-popover shadow-lg",
        )}
      >
        {isLoading ? (
          <div className="px-3 py-2 text-[12px] text-ink-3">
            {i18n._(msg`Carico colleghi…`)}
          </div>
        ) : candidates.length === 0 ? (
          <div className="px-3 py-2 text-[12px] text-ink-3">
            {resolvedNoMatchesLabel}
          </div>
        ) : (
          candidates.map((m, idx) => {
            const isActive = idx === activeIndex;
            const isSelected = m.user_id === value;
            return (
              <div
                key={m.id}
                id={`${listboxId}-opt-${m.id}`}
                role="option"
                aria-selected={isSelected}
                className={cn(
                  "flex cursor-pointer items-center gap-2 px-2.5 py-2",
                  // Neutral wash: ``orange-soft/40`` painted 40% orange (the
                  // ``/NN`` alpha trap, DESIGN §2) on every hovered row.
                  isActive && "bg-black/[0.04]",
                )}
                onMouseEnter={() => setActiveIndex(idx)}
                onMouseDown={(e) => {
                  // Use mousedown so the click registers BEFORE the
                  // input's blur (which would close the listbox).
                  e.preventDefault();
                  pick(idx);
                }}
              >
                <span
                  aria-hidden
                  className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ink-1/[0.06] font-medium text-[10px] text-ink-2"
                >
                  {_initials(m) || <User className="h-3 w-3" />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-[12.5px] text-ink-1">
                    {_label(m, i18n)}
                  </p>
                  {m.email && m.email !== _label(m, i18n) ? (
                    <p className="truncate text-[10.5px] text-ink-3">
                      {m.email}
                    </p>
                  ) : null}
                </div>
                {isSelected ? (
                  <Check
                    className="h-3.5 w-3.5 shrink-0 text-ando-orange"
                    aria-hidden
                  />
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

OrganizationMemberPicker.displayName = "OrganizationMemberPicker";
